import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns'
import { TrendingUp, DollarSign, Users, Calendar, Star, Clock, BarChart2, RefreshCw, Lock } from 'lucide-react'

// ─── Mini Bar Chart ───────────────────────────────────────────────────────────
function BarChart({ data, color = 'var(--teal)', height = 80 }) {
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <div style={{
            width: '100%', borderRadius: '3px 3px 0 0', transition: 'height 0.5s ease',
            height: `${Math.max((d.value / max) * height, d.value > 0 ? 4 : 0)}px`,
            background: color, opacity: d.isToday ? 1 : 0.5,
            minHeight: d.value > 0 ? 4 : 0,
          }} title={`${d.label}: ${d.value}`} />
          {data.length <= 14 && (
            <div style={{ fontSize: '0.55rem', color: 'var(--text-light)', textAlign: 'center', whiteSpace: 'nowrap' }}>
              {d.shortLabel || d.label}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color, trend }) {
  return (
    <div className="stat-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div className="stat-label">{label}</div>
          <div className="stat-value" style={{ color: color || 'var(--text-dark)', fontSize: '1.75rem' }}>{value}</div>
          {sub && <div className="stat-sub">{sub}</div>}
          {trend !== undefined && (
            <div style={{ fontSize: '0.72rem', marginTop: 4, color: trend >= 0 ? 'var(--teal)' : 'var(--coral)', fontWeight: 600 }}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last period
            </div>
          )}
        </div>
        {Icon && (
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color || 'var(--teal)'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={20} color={color || 'var(--teal)'} />
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Analytics Page ──────────────────────────────────────────────────────
export default function AdminAnalytics() {
  const { user, profile } = useAuth()

  if (profile?.plan_tier === 'starter' || profile?.plan_tier === 'standard') return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '48px 24px' }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Lock size={26} color="var(--coral)" />
        </div>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, color: '#0B1E3D', marginBottom: 10 }}>
          Analytics is a Pro Feature
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 28 }}>
          Upgrade to WaveSync Pro to unlock revenue charts, lesson breakdowns, instructor performance metrics, and full analytics.
        </p>
        <a
          href="mailto:hello@wavesync.app?subject=Upgrade to Pro"
          style={{ display: 'inline-block', padding: '13px 32px', background: '#E8614A', color: 'white', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none', fontFamily: 'DM Sans, sans-serif', boxShadow: '0 4px 14px rgba(232,97,74,0.3)' }}
        >
          Upgrade to Pro
        </a>
      </div>
    </div>
  )
  const [range, setRange] = useState('month') // week | month | year
  const [lessons, setLessons] = useState([])
  const [instructors, setInstructors] = useState([])
  const [loading, setLoading] = useState(true)
  const [schoolId, setSchoolId] = useState(null)

  useEffect(() => {
    if (!user) return
    loadData()
  }, [user, range])

  const loadData = async () => {
    setLoading(true)
    const { data: prof } = await supabase.from('profiles').select('school_id').eq('id', user.id).single()
    if (!prof?.school_id) { setLoading(false); return }
    setSchoolId(prof.school_id)

    const now = new Date()
    let startDate, endDate
    if (range === 'week') { startDate = startOfWeek(now, { weekStartsOn: 1 }); endDate = endOfWeek(now, { weekStartsOn: 1 }) }
    else if (range === 'month') { startDate = startOfMonth(now); endDate = endOfMonth(now) }
    else { startDate = new Date(now.getFullYear(), 0, 1); endDate = new Date(now.getFullYear(), 11, 31) }

    const [{ data: ls }, { data: ins }] = await Promise.all([
      supabase.from('lessons').select(`
        *, instructor:instructors(id, hourly_rate, profile:profiles(full_name))
      `)
      .eq('school_id', prof.school_id)
      .gte('scheduled_date', format(startDate, 'yyyy-MM-dd'))
      .lte('scheduled_date', format(endDate, 'yyyy-MM-dd'))
      .neq('status', 'cancelled'),
      supabase.from('instructors').select(`*, profile:profiles(full_name)`).eq('school_id', prof.school_id).eq('approved', true)
    ])

    setLessons(ls || [])
    setInstructors(ins || [])
    setLoading(false)
  }

  // ── Computed metrics ────────────────────────────────────────────────────────
  const totalRevenue = lessons.reduce((a, l) => a + (l.price || 0), 0)
  const totalStudents = lessons.reduce((a, l) => a + (l.num_students || 1), 0)
  const completedLessons = lessons.filter(l => l.status === 'completed').length
  const avgRating = instructors.length ? (instructors.reduce((a, i) => a + (i.rating || 0), 0) / instructors.length).toFixed(1) : '—'

  // Revenue by day
  const now = new Date()
  const days = range === 'week' ? 7 : range === 'month' ? 30 : 365
  const dayLabels = eachDayOfInterval({
    start: subDays(now, days - 1), end: now
  })
  const revenueByDay = dayLabels.map(day => {
    const dayStr = format(day, 'yyyy-MM-dd')
    const dayLessons = lessons.filter(l => l.scheduled_date === dayStr)
    return {
      label: format(day, 'MMM d'),
      shortLabel: format(day, range === 'year' ? 'MMM' : 'EEE'),
      value: dayLessons.reduce((a, l) => a + (l.price || 0), 0),
      isToday: format(day, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')
    }
  })

  // Lesson type breakdown
  const typeCount = {}
  lessons.forEach(l => { typeCount[l.lesson_type] = (typeCount[l.lesson_type] || 0) + 1 })
  const typeData = Object.entries(typeCount).sort((a, b) => b[1] - a[1])
  const typeColors = { beginner: 'var(--teal)', intermediate: 'var(--ocean-bright)', advanced: 'var(--coral)', kids: 'var(--gold)', private: 'var(--navy)', group: '#7850C8' }

  // Busiest days of week
  const dowCount = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 }
  lessons.forEach(l => {
    const dow = new Date(l.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3)
    if (dowCount[dow] !== undefined) dowCount[dow]++
  })
  const dowData = Object.entries(dowCount).map(([label, value]) => ({ label, shortLabel: label, value, isToday: false }))

  // Instructor utilization
  const instStats = instructors.map(inst => {
    const instLessons = lessons.filter(l => l.instructor_id === inst.id)
    const hours = instLessons.reduce((a, l) => {
      if (!l.start_time || !l.end_time) return a
      const [sh, sm] = l.start_time.split(':').map(Number)
      const [eh, em] = l.end_time.split(':').map(Number)
      return a + (eh * 60 + em - sh * 60 - sm) / 60
    }, 0)
    const pay = hours * (inst.hourly_rate || 0)
    return { ...inst, lessonCount: instLessons.length, hours: hours.toFixed(1), pay: pay.toFixed(0) }
  }).sort((a, b) => b.lessonCount - a.lessonCount)

  // Customer retention
  const customerMap = {}
  lessons.forEach(l => {
    if (!l.customer_id) return
    customerMap[l.customer_id] = (customerMap[l.customer_id] || 0) + 1
  })
  const repeatCustomers = Object.values(customerMap).filter(c => c > 1).length
  const totalCustomers = Object.keys(customerMap).length
  const retentionRate = totalCustomers ? Math.round((repeatCustomers / totalCustomers) * 100) : 0

  // Peak hours
  const hourCount = {}
  lessons.forEach(l => {
    if (!l.start_time) return
    const h = parseInt(l.start_time.split(':')[0])
    const label = h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`
    hourCount[label] = (hourCount[label] || 0) + 1
  })
  const peakHour = Object.entries(hourCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '—'

  const rangeLabel = range === 'week' ? 'This Week' : range === 'month' ? 'This Month' : 'This Year'

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  return (
    <div>
      {/* Range selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 700 }}>Analytics</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {['week', 'month', 'year'].map(r => (
            <button key={r} onClick={() => setRange(r)} className={`btn btn-sm ${range === r ? 'btn-navy' : 'btn-ghost'}`} style={{ textTransform: 'capitalize' }}>
              {r === 'week' ? 'This Week' : r === 'month' ? 'This Month' : 'This Year'}
            </button>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={loadData}><RefreshCw size={13} /></button>
        </div>
      </div>

      {/* Top stats */}
      <div className="stats-grid mb-6">
        <StatCard label="Total Revenue" value={`$${totalRevenue.toLocaleString()}`} sub={rangeLabel} icon={DollarSign} color="var(--teal)" />
        <StatCard label="Total Lessons" value={lessons.length} sub={`${completedLessons} completed`} icon={Calendar} color="var(--ocean-bright)" />
        <StatCard label="Total Students" value={totalStudents} sub={`${totalCustomers} unique customers`} icon={Users} color="var(--coral)" />
        <StatCard label="Avg Rating" value={`★ ${avgRating}`} sub="Across all instructors" icon={Star} color="var(--gold)" />
        <StatCard label="Retention Rate" value={`${retentionRate}%`} sub={`${repeatCustomers} repeat customers`} icon={TrendingUp} color="var(--teal)" />
        <StatCard label="Peak Hour" value={peakHour} sub="Most booked time" icon={Clock} color="var(--navy)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
        {/* Revenue chart */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontWeight: 600 }}>Revenue — {rangeLabel}</h3>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 700, color: 'var(--teal)' }}>${totalRevenue.toLocaleString()}</span>
          </div>
          <div className="card-body">
            {revenueByDay.every(d => d.value === 0) ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)', fontSize: '0.875rem' }}>No revenue data for this period</div>
            ) : (
              <BarChart data={revenueByDay.slice(-30)} color="var(--teal)" height={100} />
            )}
          </div>
        </div>

        {/* Busiest days */}
        <div className="card">
          <div className="card-header"><h3 style={{ fontWeight: 600 }}>Busiest Days of Week</h3></div>
          <div className="card-body">
            <BarChart data={dowData} color="var(--ocean-bright)" height={100} />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
        {/* Lesson types */}
        <div className="card">
          <div className="card-header"><h3 style={{ fontWeight: 600 }}>Lesson Types</h3></div>
          <div className="card-body">
            {typeData.length === 0 ? (
              <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>No lessons yet.</p>
            ) : typeData.map(([type, count]) => {
              const pct = Math.round((count / lessons.length) * 100)
              return (
                <div key={type} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.825rem' }}>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{type}</span>
                    <span style={{ color: 'var(--text-light)' }}>{count} lessons · {pct}%</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(11,30,61,0.07)', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: typeColors[type] || 'var(--teal)', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Customer retention */}
        <div className="card">
          <div className="card-header"><h3 style={{ fontWeight: 600 }}>Customer Breakdown</h3></div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { label: 'Total Unique Customers', value: totalCustomers, color: 'var(--navy)' },
                { label: 'Repeat Customers', value: repeatCustomers, color: 'var(--teal)' },
                { label: 'One-time Customers', value: totalCustomers - repeatCustomers, color: 'var(--text-light)' },
                { label: 'Retention Rate', value: `${retentionRate}%`, color: retentionRate >= 40 ? 'var(--teal)' : retentionRate >= 20 ? 'var(--gold)' : 'var(--coral)' },
                { label: 'Avg Lessons per Customer', value: totalCustomers ? (lessons.length / totalCustomers).toFixed(1) : '—', color: 'var(--ocean-bright)' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid rgba(11,30,61,0.05)' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-mid)' }}>{s.label}</span>
                  <span style={{ fontSize: '1rem', fontWeight: 700, color: s.color }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Instructor performance */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ fontWeight: 600 }}>Instructor Performance</h3>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{rangeLabel}</span>
        </div>
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px 100px', padding: '0.5rem 1.5rem', background: 'rgba(11,30,61,0.02)', borderBottom: '1px solid rgba(11,30,61,0.07)', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em', gap: '1rem' }}>
            <div>Instructor</div><div>Lessons</div><div>Hours</div><div>Rating</div><div>Load</div><div style={{ textAlign: 'right' }}>Est. Pay</div>
          </div>
          {instStats.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-light)', fontSize: '0.875rem' }}>No approved instructors yet.</div>
          ) : instStats.map(inst => {
            const maxLessons = Math.max(...instStats.map(i => i.lessonCount), 1)
            const loadPct = Math.round((inst.lessonCount / maxLessons) * 100)
            const initials = (inst.profile?.full_name || '').split(' ').map(n => n[0]).join('').slice(0, 2)
            return (
              <div key={inst.id} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px 100px', padding: '0.875rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)', alignItems: 'center', gap: '1rem', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(11,30,61,0.02)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="avatar" style={{ width: 32, height: 32, background: 'var(--ocean)', fontSize: '0.65rem' }}>{initials}</div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{inst.profile?.full_name}</span>
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{inst.lessonCount}</div>
                <div style={{ fontSize: '0.875rem' }}>{inst.hours}h</div>
                <div style={{ fontSize: '0.875rem' }}>★ {inst.rating?.toFixed(1)}</div>
                <div>
                  <div style={{ height: 5, background: 'rgba(11,30,61,0.07)', borderRadius: 3, width: 60 }}>
                    <div style={{ height: '100%', width: `${loadPct}%`, borderRadius: 3, background: loadPct > 80 ? 'var(--coral)' : loadPct > 60 ? 'var(--gold)' : 'var(--teal)' }} />
                  </div>
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--teal)', textAlign: 'right' }}>${inst.pay}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
